import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';

import { AppState } from '.';
import { User } from '../models';
import { Operator } from '../models/query';
import { UserService } from '../services';

export const USER_FEATURE_KEY = 'users';
interface UsersState extends EntityState<User> {
  loading: boolean;
  error?: string;
  selectedId: User['id'] | null;
  count: number;
  isAdminCreated?: boolean;
  submitting: boolean;
}

const usersAdapter = createEntityAdapter<User>();

export const createInitialState = (): UsersState => usersAdapter.getInitialState({
  loading: false,
  selectedId: null,
  selectedEmail: null,
  count: 0,
  isAdminCreated: false,
  submitting: false,
});

export const getUsers = createAsyncThunk(
  'users/getUsers',
  async ({ offset, limit, email }: { offset: number, limit: number, email?: User['email'] }) => {
    const users = email ? await UserService.getUsers(
      { field: 'archived', operator: Operator.EQ, value: false },
      { field: 'email', operator: Operator.EQ, value: email },
      { offset },
      { limit },
    ) : await UserService.getUsers(
      { field: 'archived', operator: Operator.EQ, value: false },
      { offset },
      { limit },
    );
    return { users: users.data, count: users.total };
  },
);

export const doFetchUserById = createAsyncThunk(
  'users/doFetchUserById',
  async (input: { id: User['id'] }) => {
    const users = await UserService.queryUsers(
      { field: 'archived', operator: Operator.EQ, value: false },
      { field: 'id', operator: Operator.EQ, value: input.id },
    );
    return { users: users.data, count: users.total };
  },
);

export const doCheckAdminCreated = createAsyncThunk(
  'users/checkAdminCreated',
  async () => {
    const response = await UserService.checkAdminCreated();
    return { isAdminCreated: response.data.userExists };
  },
);

const usersSlice = createSlice({
  name: USER_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    doChangeSelectedId(state, action: PayloadAction<Pick<User, 'id'>>) {
      state.selectedId = action.payload.id;
    },
  },
  extraReducers: builder => {
    builder.addCase(getUsers.pending, state => {
      state.loading = true;
    });
    builder.addCase(getUsers.fulfilled, (state, action) => {
      usersAdapter.removeAll(state);
      state.loading = false;
      state.count = action.payload.count;
      usersAdapter.addMany(state, action.payload.users);
    });
    builder.addCase(getUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(doFetchUserById.pending, state => {
      state.loading = true;
    });
    builder.addCase(doFetchUserById.fulfilled, (state, action) => {
      usersAdapter.removeAll(state);
      state.loading = false;
      state.count = action.payload.count;
      usersAdapter.addMany(state, action.payload.users);
    });
    builder.addCase(doFetchUserById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(doCheckAdminCreated.fulfilled, (state, action) => {
      state.loading = false;
      state.isAdminCreated = action.payload.isAdminCreated;
    });
    builder.addCase(doCheckAdminCreated.pending, state => {
      state.loading = true;
    });
    builder.addCase(doCheckAdminCreated.rejected, (state, action) => {
      state.loading = true;
      state.error = action.error.message;
    });
  },
});

const selectUsersFeature = (state: AppState) => state[USER_FEATURE_KEY];
const { selectAll, selectEntities } = usersAdapter.getSelectors(selectUsersFeature);

export const selectSelectedId = createSelector(
  selectUsersFeature,
  usersState => usersState.selectedId,
);
export const selectAllUsers = selectAll;
export const selectSelectedUser = createSelector(
  selectEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : null),
);

export default usersSlice.reducer;
